// BrandCalendar.js
import React, { useEffect, useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Calendar } from 'react-native-calendars';
import { format, differenceInCalendarDays } from 'date-fns';
import { LinearGradient } from 'expo-linear-gradient';
import { Linking } from 'react-native';

const BrandCalendar = ({ navigation }) => {
  const [collabs, setCollabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markedDates, setMarkedDates] = useState({});
  const [statusFilter, setStatusFilter] = useState('All'); // All | Upcoming | Redeemed | Expired | Pending | Cancelled
  const [sortOrder, setSortOrder] = useState('soon'); // soon | latest

  // NEW: session + auth state for sidenav
  const [userData, setUserData] = useState({});
  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState('none'); // "subscriber" | "none"
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === 'subscriber';

  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/GetCollabCalendar.php', {
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.message) {
          setCollabs([]);
        } else {
          setCollabs(data);
        }
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php', {
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setUserData(data);
      })
      .catch((error) => {
        console.error('Error fetching user data:', error);
      });
  }, []);

  // Subscription/auth state for sidenav
  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data.authenticated === 'boolean'
            ? data.authenticated
            : data.status === 'active';
        const mode = data.auth_mode
          ? data.auth_mode
          : data.status === 'active'
          ? 'subscriber'
          : 'none';

        setIsAuthed(!!authed);
        setAuthMode(mode);
      })
      .catch((err) => console.error('Error fetching auth state:', err))
      .finally(() => setAuthChecked(true));
  }, []);

  // ---- HELPERS ----
  const openInSameTab = (url) => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  const getCollabStatus = (collab) => {
    if (collab.Status === 'Cancelled') {
      return 'Cancelled';
    }

    const today = new Date();
    const creditAvailableDate = new Date(collab.CreditAvailableStart);

    if (creditAvailableDate < today) {
      // already past
      return collab.Redeemed ? 'Redeemed' : 'Expired';
    } else {
      return collab.Status === 'Accepted' ? 'Upcoming' : 'Pending';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix =
      day === 1 || day === 21 || day === 31
        ? 'st'
        : day === 2 || day === 22
        ? 'nd'
        : day === 3 || day === 23
        ? 'rd'
        : 'th';
    const month = format(date, 'MMM');
    return `${day}${suffix} ${month}`;
  };

  const calculateDaysDifference = (dateString) => {
    const today = new Date();
    const targetDate = new Date(dateString);
    const diffDays = differenceInCalendarDays(targetDate, today);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'In 1 day';
    if (diffDays > 1) return `In ${diffDays} days`;
    if (diffDays === -1) return '1 day ago';
    return `${Math.abs(diffDays)} days ago`;
  };

  const handleBackPress = () => {
    navigation.goBack();
  };

  const handleCardPress = (collab) => {
    navigation.navigate('BookingRequest', {
      CollabID: collab.CollabID,
      Promoter: collab.Promoter,
      CreditAvailableStart: collab.CreditAvailableStart,
    });
  };

  // ---- COLOUR-CODED CALENDAR MARKS ----
  useEffect(() => {
    if (!collabs || collabs.length === 0) {
      setMarkedDates({});
      return;
    }

    const statusPriority = {
      Redeemed: 5,
      Upcoming: 4,
      Pending: 3,
      Expired: 2,
      Cancelled: 1,
    };

    const statusColorMap = {
      Upcoming: '#74dc8a',
      Pending: '#FDF05D',
      Redeemed: '#4CAF50',
      Expired: '#b0b0b0',
      Cancelled: '#ff6b6b',
    };

    const dates = {};

    collabs.forEach((collab) => {
      const dateKey = format(
        new Date(collab.CreditAvailableStart),
        'yyyy-MM-dd'
      );
      const status = getCollabStatus(collab);
      const priority = statusPriority[status] || 1;

      if (!dates[dateKey] || priority > (dates[dateKey].priority || 0)) {
        dates[dateKey] = {
          marked: true,
          dotColor: '#000000',
          selected: true,
          selectedColor: statusColorMap[status] || '#FDF05D',
          selectedTextColor: '#111',
          priority,
        };
      }
    });

    // remove priority before passing to Calendar
    const cleaned = {};
    Object.keys(dates).forEach((key) => {
      const { priority, ...rest } = dates[key];
      cleaned[key] = rest;
    });

    setMarkedDates(cleaned);
  }, [collabs]);

  // ---- FILTER + SORT ----
  const filteredSortedCollabs = useMemo(() => {
    if (!collabs) return [];

    let arr = collabs.map((c) => ({
      ...c,
      _status: getCollabStatus(c),
    }));

    if (statusFilter !== 'All') {
      arr = arr.filter((c) => c._status === statusFilter);
    }

    arr.sort((a, b) => {
      const aDate = new Date(a.CreditAvailableStart);
      const bDate = new Date(b.CreditAvailableStart);
      const diff = aDate - bDate;
      return sortOrder === 'soon' ? diff : -diff;
    });

    return arr;
  }, [collabs, statusFilter, sortOrder]);

  // ⏳ LOADING STATE — shell but no sidenav
  if (loading) {
    return (
      <View style={styles.root}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.shellRow}>
            <View style={styles.mainArea}>
              <View style={[styles.loadingCenter, { flex: 1 }]}>
                <ActivityIndicator size="large" color="#111827" />
                <Text style={styles.loadingText}>Loading calendar…</Text>
              </View>
            </View>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  // MAIN RENDER — with sidenav + calendar content
  return (
    <View style={styles.root}>
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.shellRow}>
          {/* SIDE NAV */}
          <View style={styles.sideNav}>
            <LinearGradient
              colors={['#050509', '#050505', '#000000']}
              style={styles.sideNavInner}
            >
              {/* subtle glow blob */}
              <LinearGradient
                colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
                style={styles.sideNavGlow}
                start={{ x: 0.1, y: 0 }}
                end={{ x: 1, y: 1 }}
              />

              {/* TOP: Logo + wordmark */}
              <View style={styles.sideNavHeader}>
                <Image
                  source={{
                    uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
                  }}
                  style={styles.sideNavLogoImage}
                  resizeMode="contain"
                />
                <View>
                  <Text style={styles.logoWordmark}>
                    <Text style={{ fontWeight: '300' }}>collab</Text>
                    bay
                    <Text style={styles.logoDot}>.</Text>
                  </Text>
                  <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                </View>
              </View>

              {/* MAIN NAV SECTION */}
              <View style={styles.sideNavSection}>
                {/* Username pill */}
                <LinearGradient
                  colors={['#111111', '#2b2b2b']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.usernamePill}
                >
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={styles.usernamePillText}
                  >
                    {userData.Username ? ` ${userData.Username}` : 'Welcome Back'}
                  </Text>
                </LinearGradient>

                {/* Home → MyPanel */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('MyPanel')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="home-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Home</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Offers → MyOffers */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('MyOffers')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="pricetags-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Offers</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Calendar → MyCalendar (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab('https://collabbayadmin.com/subscribe');
                    } else {
                      navigation.navigate('MyCalendar');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="calendar-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Calendar</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Browse Creators → BrowseCreators */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('BrowseCreators')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="people-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Browse Creators</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* Activity → ActivityFeed (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab('https://collabbayadmin.com/subscribe');
                    } else {
                 navigation.navigate('MyActivityFeed');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="chatbubbles-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Activity</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Account → BusinessAccountPage */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('BusinessAccountPage')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="person-circle-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Account</Text>
                    </View>
                  </View>
                </TouchableOpacity>
              </View>

              {/* MIDDLE SECTION: Peer Map + Subscribe CTA */}
              <View style={styles.sideNavSection}>
                {/* Peer Map */}
                <TouchableOpacity
                  style={styles.peerMapCard}
                  activeOpacity={0.9}
                  onPress={() => openInSameTab('/venues')}
                >
                  <LinearGradient
                    colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
                    style={styles.peerMapInner}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <View style={styles.peerMapContent}>
                      <Ionicons
                        name="map-outline"
                        size={18}
                        color="#FDF05D"
                        style={{ marginRight: 8 }}
                      />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.peerMapTitle}>Peer Map</Text>
                        <Text style={styles.peerMapSubtitle}>
                          View 100+ venues on Collabbay
                        </Text>
                      </View>
                    </View>
                  </LinearGradient>
                </TouchableOpacity>

                {/* Subscribe CTA — only if NOT subscriber */}
                {!isSubscriber && (
                  <TouchableOpacity
                    style={styles.subscribeCard}
                    activeOpacity={0.9}
                    onPress={() =>
                      openInSameTab('https://collabbayadmin.com/subscribe')
                    }
                  >
                    <LinearGradient
                      colors={['#FDF05D', '#fbbf24']}
                      style={styles.subscribeGradient}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                    >
                      <View style={styles.subscribeContent}>
                        <Ionicons
                          name="rocket-outline"
                          size={20}
                          color="#000"
                          style={{ marginRight: 8 }}
                        />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.subscribeTitle}>
                            Subscribe & unlock
                          </Text>
                          <Text style={styles.subscribeSubtitle}>
                            Unlock Activity & Calendar, plus more.
                          </Text>
                        </View>
                      </View>
                    </LinearGradient>
                  </TouchableOpacity>
                )}
              </View>

              {/* BOTTOM: FAQ + Help */}
              <View style={styles.sideNavBottom}>
                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() =>
                    openInSameTab('https://collabbayadmin.com/FAQs')
                  }
                >
                  <Ionicons
                    name="help-circle-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>FAQs</Text>
                    <Text style={styles.bottomLinkCaption}>
                      Common questions answered
                    </Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() =>
                    Linking.openURL('mailto:info@collab-bay.com')
                  }
                >
                  <Ionicons
                    name="headset-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>Help</Text>
                    <Text style={styles.bottomLinkCaption}>
                      We&apos;re here 24/7
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>

          {/* MAIN AREA */}
          <View style={styles.mainArea}>
            <ScrollView
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.pageInner}>
                {/* TOP NAV / BANNER */}
                <View style={styles.pageHeaderRow}>
                  <TouchableOpacity
                    style={styles.backButton}
                    onPress={handleBackPress}
                  >
                    <Ionicons name="arrow-back" size={20} color="black" />
                    <Text style={styles.backButtonText}>Back to activity</Text>
                  </TouchableOpacity>

                  <View style={styles.bannerRight}>
                    <Text style={styles.smallWelcome}>
                      {userData.Username
                        ? `Hi, ${userData.Username}`
                        : 'Brand account'}
                    </Text>
                  </View>
                </View>

                <View style={styles.bannerCard}>
                  <View>
                    <Text style={styles.bannerTitle}>Booking calendar</Text>
                    <Text style={styles.bannerSubtitle}>
                      See all creator bookings at a glance — calendar on top,
                      grid view below.
                    </Text>
                  </View>
                  <Ionicons name="calendar-outline" size={28} color="black" />
                </View>

                {/* CALENDAR */}
                <View style={styles.calendarWrapper}>
                  <Calendar
                    markedDates={markedDates}
                    onDayPress={(day) => {
                      const selectedCollabs = filteredSortedCollabs.filter(
                        (collab) =>
                          format(
                            new Date(collab.CreditAvailableStart),
                            'yyyy-MM-dd'
                          ) === day.dateString
                      );
                      if (selectedCollabs.length > 0) {
                        handleCardPress(selectedCollabs[0]);
                      }
                    }}
                    enableSwipeMonths={true}
                    hideExtraDays={false}
                    renderArrow={(direction) => (
                      <Ionicons
                        name={
                          direction === 'left'
                            ? 'chevron-back'
                            : 'chevron-forward'
                        }
                        size={20}
                        color="#000"
                      />
                    )}
                    onPressArrowLeft={(subtractMonth) => subtractMonth()}
                    onPressArrowRight={(addMonth) => addMonth()}
                    theme={{
                      backgroundColor: '#ffffff',
                      calendarBackground: '#ffffff',
                      textSectionTitleColor: '#888',
                      selectedDayBackgroundColor: '#FDF05D',
                      selectedDayTextColor: '#111',
                      todayTextColor: '#000',
                      dayTextColor: '#222',
                      monthTextColor: '#111',
                      arrowColor: '#000',
                      textDayFontWeight: '600',
                      textMonthFontWeight: '700',
                      textDayHeaderFontWeight: '600',
                    }}
                    style={styles.calendar}
                  />
                </View>

                {/* FILTERS + SORT */}
                <View style={styles.filterSortRow}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={styles.filterChipsRow}
                  >
                    {[
                      'All',
                      'Upcoming',
                      'Redeemed',
                      'Expired',
                      'Pending',
                      'Cancelled',
                    ].map((status) => (
                      <TouchableOpacity
                        key={status}
                        style={[
                          styles.filterChip,
                          statusFilter === status && styles.filterChipActive,
                        ]}
                        onPress={() => setStatusFilter(status)}
                      >
                        <Text
                          style={[
                            styles.filterChipText,
                            statusFilter === status &&
                              styles.filterChipTextActive,
                          ]}
                        >
                          {status}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>

                  <View style={styles.sortControls}>
                    <Text style={styles.sortLabel}>Sort</Text>
                    <TouchableOpacity
                      style={[
                        styles.sortOption,
                        sortOrder === 'soon' && styles.sortOptionActive,
                      ]}
                      onPress={() => setSortOrder('soon')}
                    >
                      <Ionicons
                        name="trending-up-outline"
                        size={14}
                        color={sortOrder === 'soon' ? '#000' : '#666'}
                        style={{ marginRight: 4 }}
                      />
                      <Text
                        style={[
                          styles.sortOptionText,
                          sortOrder === 'soon' &&
                            styles.sortOptionTextActive,
                        ]}
                      >
                        Soonest
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.sortOption,
                        sortOrder === 'latest' && styles.sortOptionActive,
                      ]}
                      onPress={() => setSortOrder('latest')}
                    >
                      <Ionicons
                        name="time-outline"
                        size={14}
                        color={sortOrder === 'latest' ? '#000' : '#666'}
                        style={{ marginRight: 4 }}
                      />
                      <Text
                        style={[
                          styles.sortOptionText,
                          sortOrder === 'latest' &&
                            styles.sortOptionTextActive,
                        ]}
                      >
                        Latest
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>

                {/* GRID TITLE */}
                <View style={styles.gridHeaderRow}>
                  <Text style={styles.gridHeaderTitle}>
                    Upcoming & past collabs
                  </Text>
                  <Text style={styles.gridHeaderMeta}>
                    {filteredSortedCollabs.length} booking
                    {filteredSortedCollabs.length === 1 ? '' : 's'}
                  </Text>
                </View>

                {/* GRID OF CARDS (4 PER ROW) */}
                {filteredSortedCollabs.length === 0 ? (
                  <Text style={styles.noResults}>
                    No collabs to show for this view.
                  </Text>
                ) : (
                  <View style={styles.gridContainer}>
                    {filteredSortedCollabs.map((collab) => {
                      const status = collab._status;
                      const daysText = calculateDaysDifference(
                        collab.CreditAvailableStart
                      );
                      const plusOnes =
                        collab.AddedVisitors === null ||
                        collab.AddedVisitors === undefined
                          ? 0
                          : collab.AddedVisitors;
                      const onBehalfOf =
                        collab.OnBehalfOf && collab.OnBehalfOf.trim() !== ''
                          ? collab.OnBehalfOf.trim()
                          : null;

                      return (
                        <TouchableOpacity
                          key={collab.CollabID}
                          style={styles.card}
                          onPress={() => handleCardPress(collab)}
                          activeOpacity={0.85}
                        >
                          {/* STATUS BADGE */}
                          <View
                            style={[
                              styles.statusBadge,
                              status === 'Upcoming' &&
                                styles.upcomingStatus,
                              status === 'Redeemed' &&
                                styles.redeemedStatus,
                              status === 'Expired' && styles.expiredStatus,
                              status === 'Pending' && styles.pendingStatus,
                              status === 'Cancelled' &&
                                styles.cancelledStatus,
                            ]}
                          >
                            <Text style={styles.statusText}>{status}</Text>
                          </View>

                          <View style={styles.cardInner}>
                            {/* TOP: DATE + RELATIVE */}
                            <View style={styles.cardTopRow}>
                              <View>
                                <Text style={styles.cardDate}>
                                  {formatDate(collab.CreditAvailableStart)}
                                </Text>
                                <Text style={styles.cardRelative}>
                                  {daysText}
                                </Text>
                              </View>
                            </View>

                            {/* PROMOTER / CLIENT */}
                            <View style={styles.cardMiddle}>
                              <Text style={styles.cardPromoter}>
                                {collab.Promoter}
                              </Text>
                              {onBehalfOf && (
                                <View style={styles.clientChip}>
                                  <Ionicons
                                    name="person-circle-outline"
                                    size={13}
                                    color="#555"
                                    style={{ marginRight: 4 }}
                                  />
                                  <Text style={styles.clientChipLabel}>
                                    Client
                                  </Text>
                                  <Text style={styles.clientChipValue}>
                                    {onBehalfOf}
                                  </Text>
                                </View>
                              )}
                            </View>

                            {/* META ROW: PLUS ONES ONLY */}
                            <View style={styles.metaRow}>
                              <View style={styles.metaChip}>
                                <Ionicons
                                  name="people-outline"
                                  size={13}
                                  color="#333"
                                  style={{ marginRight: 4 }}
                                />
                                <Text style={styles.metaText}>
                                  Plus-ones: {plusOnes}
                                </Text>
                              </View>
                            </View>

                            {/* FOOTER: ARROW BOTTOM RIGHT */}
                            <View style={styles.cardFooterRow}>
                              <Ionicons
                                name="chevron-forward"
                                size={18}
                                color="#999"
                              />
                            </View>
                          </View>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                )}
              </View>
            </ScrollView>
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  // SHELL
  root: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  safeArea: {
    flex: 1,
  },
  shellRow: {
    flex: 1,
    flexDirection: 'row',
  },

  // MAIN AREA
  mainArea: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  scrollContent: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    alignItems: 'center',
    alignSelf: 'center',
  },

  // LOADING
  loadingCenter: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 8,
    color: '#555',
    fontSize: 14,
  },

  // PAGE INNER
  pageInner: {
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
  },

  // HEADER / BANNER
  pageHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 999,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  backButtonText: {
    marginLeft: 6,
    fontSize: 13,
    color: '#333',
    fontWeight: '500',
  },
  bannerRight: {
    alignItems: 'flex-end',
  },
  smallWelcome: {
    fontSize: 12,
    color: '#555',
  },
  bannerCard: {
    backgroundColor: '#FDF05D',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bannerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111',
    marginBottom: 4,
  },
  bannerSubtitle: {
    fontSize: 13,
    color: '#333',
  },

  // CALENDAR
  calendarWrapper: {
    marginBottom: 18,
    backgroundColor: '#fff',
    borderRadius: 14,
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 5,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  calendar: {
    width: '100%',
    paddingVertical: 10,
    borderRadius: 14,
  },

  // FILTER + SORT
  filterSortRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'spaceBetween',
    marginBottom: 10,
  },
  filterChipsRow: {
    paddingRight: 8,
  },
  filterChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#ddd',
    marginRight: 6,
    backgroundColor: '#fff',
  },
  filterChipActive: {
    backgroundColor: '#000',
    borderColor: '#000',
  },
  filterChipText: {
    fontSize: 12,
    color: '#555',
    fontWeight: '500',
  },
  filterChipTextActive: {
    color: '#fff',
  },
  sortControls: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sortLabel: {
    fontSize: 12,
    color: '#555',
    marginRight: 6,
  },
  sortOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#ddd',
    marginLeft: 4,
    backgroundColor: '#fff',
  },
  sortOptionActive: {
    backgroundColor: '#FDF05D',
    borderColor: '#000',
  },
  sortOptionText: {
    fontSize: 12,
    color: '#555',
  },
  sortOptionTextActive: {
    color: '#000',
    fontWeight: '600',
  },

  // GRID HEADER
  gridHeaderRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginTop: 6,
    marginBottom: 8,
  },
  gridHeaderTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111',
  },
  gridHeaderMeta: {
    fontSize: 12,
    color: '#777',
  },

  // GRID
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  card: {
    width: '25%', // 4 per row
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 10,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#eee',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 3,
    shadowOffset: { width: 0, height: 1 },
    elevation: 2,
    position: 'relative',
  },
  cardInner: {
    flex: 1,
  },
  cardTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  cardDate: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111',
  },
  cardRelative: {
    fontSize: 11,
    color: '#777',
    marginTop: 2,
  },
  cardMiddle: {
    marginBottom: 8,
  },
  cardPromoter: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111',
    marginBottom: 4,
  },
  clientChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    alignSelf: 'flex-start',
  },
  clientChipLabel: {
    fontSize: 10,
    color: '#555',
    marginRight: 4,
  },
  clientChipValue: {
    fontSize: 11,
    color: '#111',
    fontWeight: '600',
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  metaChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f3f3',
    paddingHorizontal: 7,
    paddingVertical: 4,
    borderRadius: 999,
    marginRight: 6,
    marginTop: 4,
  },
  metaText: {
    fontSize: 11,
    color: '#333',
  },
  cardFooterRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 8,
  },

  // STATUS BADGE
  statusBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
  },
  statusText: {
    fontWeight: '600',
    fontSize: 10,
    color: '#000',
  },
  expiredStatus: {
    backgroundColor: '#d0d0d0',
  },
  redeemedStatus: {
    backgroundColor: '#a5e6b9',
  },
  upcomingStatus: {
    backgroundColor: '#74dc8a',
  },
  pendingStatus: {
    backgroundColor: '#FDF05D',
  },
  cancelledStatus: {
    backgroundColor: '#ff9b9b',
  },

  // EMPTY
  noResults: {
    fontSize: 14,
    textAlign: 'center',
    marginTop: 20,
    color: '#555',
  },

  // SIDE NAV
  sideNav: {
    width: 250,
    backgroundColor: '#000',
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  sideNavGlow: {
    position: 'absolute',
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '800',
  },
  logoDesktopTag: {
    fontSize: 9,
    color: '#b3b3b3',
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'left',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  navText: {
    color: '#f5f5f5',
    fontSize: 14,
    fontWeight: '500',
  },
  lockIcon: {
    marginLeft: 6,
  },
  peerMapCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  peerMapTitle: {
    color: '#f5f5f5',
    fontSize: 13,
    fontWeight: '600',
  },
  peerMapSubtitle: {
    color: '#b3b3b3',
    fontSize: 11,
    marginTop: 2,
  },
  subscribeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: '#1f2933',
    marginTop: 2,
  },
  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: '#f5f5f5',
    fontWeight: '600',
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: '#b3b3b3',
    marginTop: 2,
  },
});

export default BrandCalendar;
